/**
 * Fitness Fort – Mobile filter drawer above sticky header.
 * Portals the panel to body and drives smooth open/close animation.
 */
(function () {
  const BODY_CLASS = 'ff-facets-open';
  const PORTAL_CLASS = 'ff-mobile-facets-portal';
  const PANEL_OPEN_CLASS = 'ff-facets-panel-opening';
  const ANIMATION_MS = 500;

  function getPortal() {
    let portal = document.querySelector(`.${PORTAL_CLASS}`);
    if (!portal) {
      portal = document.createElement('div');
      portal.className = PORTAL_CLASS;
      portal.setAttribute('aria-hidden', 'true');
      document.body.appendChild(portal);
    }
    return portal;
  }

  function getForm(panel) {
    return panel.querySelector('form.mobile-facets');
  }

  // iOS Safari blurs the tapped <summary> right after touch-end (it doesn't
  // keep focus on non-form elements the way Android Chrome does). Dawn's
  // menu-drawer treats that blur as "focus left the drawer" and auto-closes
  // it (see MenuDrawer.onFocusOut in global.js), which is what caused the
  // drawer to open and immediately snap shut again on iPhone only. Once the
  // panel is portaled to <body> (below) that containment check is stale
  // anyway, since the real content no longer lives inside the <details>, so
  // it's safe to stop focus loss on the summary from ever reaching it. The
  // drawer still closes via its own explicit controls (close button, scrim,
  // Escape).
  document.addEventListener(
    'focusout',
    (event) => {
      if (event.target.closest('.mobile-facets__wrapper details.mobile-facets__disclosure')) {
        event.stopPropagation();
      }
    },
    true
  );

  function closeDrawer(drawer, details, panel) {
    const summary = drawer.querySelector('summary.mobile-facets__open-wrapper');
    if (!details.hasAttribute('open') || !summary) return;
    summary.click();
  }

  function bindDrawer(drawer) {
    if (drawer.dataset.ffFacetsBound === 'true') return;
    drawer.dataset.ffFacetsBound = 'true';

    const details = drawer.querySelector('details.mobile-facets__disclosure');
    const panel = drawer.querySelector('facet-filters-form');
    if (!details || !panel) return;

    let anchor = null;
    let closingTimer = null;

    const form = () => getForm(panel);
    const inner = () => form()?.querySelector('.mobile-facets__inner');

    const startOpenAnimation = () => {
      const facetsForm = form();
      if (!facetsForm) return;
      facetsForm.classList.remove(PANEL_OPEN_CLASS);
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          facetsForm.classList.add(PANEL_OPEN_CLASS);
        });
      });
    };

    const startCloseAnimation = () => {
      form()?.classList.remove(PANEL_OPEN_CLASS);
    };

    const moveToPortal = () => {
      if (panel.parentElement === getPortal()) return;
      anchor = document.createComment('ff-mobile-facets-anchor');
      panel.parentNode.insertBefore(anchor, panel);
      getPortal().appendChild(panel);
    };

    const restoreFromPortal = () => {
      if (!anchor || !anchor.parentNode) return;
      anchor.parentNode.insertBefore(panel, anchor.nextSibling);
      anchor.remove();
      anchor = null;
      form()?.classList.remove(PANEL_OPEN_CLASS);
    };

    const openDrawer = () => {
      window.clearTimeout(closingTimer);
      moveToPortal();
      document.body.classList.add(BODY_CLASS);
      document.documentElement.classList.add(BODY_CLASS);
      drawer.classList.add('ff-facets-active');
      startOpenAnimation();
    };

    const finishClose = () => {
      restoreFromPortal();
      document.body.classList.remove(BODY_CLASS);
      document.documentElement.classList.remove(BODY_CLASS);
      drawer.classList.remove('ff-facets-active');
    };

    const scheduleFinishClose = () => {
      window.clearTimeout(closingTimer);
      const panelInner = inner();
      let finished = false;

      const done = () => {
        if (finished) return;
        finished = true;
        panelInner?.removeEventListener('transitionend', onTransitionEnd);
        window.clearTimeout(closingTimer);
        finishClose();
      };

      const onTransitionEnd = (event) => {
        if (event.propertyName === 'transform') done();
      };

      if (panelInner) {
        panelInner.addEventListener('transitionend', onTransitionEnd);
      }

      closingTimer = window.setTimeout(done, ANIMATION_MS);
    };

    details.addEventListener('toggle', () => {
      if (details.hasAttribute('open')) {
        openDrawer();
        return;
      }

      startCloseAnimation();
      scheduleFinishClose();
    });

    const classObserver = new MutationObserver(() => {
      if (!details.hasAttribute('open')) return;

      if (details.classList.contains('menu-opening')) {
        form()?.classList.add(PANEL_OPEN_CLASS);
      } else {
        startCloseAnimation();
      }
    });

    classObserver.observe(details, {
      attributes: true,
      attributeFilter: ['class'],
    });

    panel.addEventListener('click', (event) => {
      if (event.target.closest('[data-ff-facets-close]')) {
        event.preventDefault();
        closeDrawer(drawer, details, panel);
        return;
      }

      if (event.target === form()) {
        closeDrawer(drawer, details, panel);
      }
    });
  }

  function init() {
    document.querySelectorAll('menu-drawer.mobile-facets__wrapper').forEach(bindDrawer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
